import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Eye, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { orpc } from '@/lib/orpcClient'
import type { HRPerformanceReview, HREmployee } from '@cloud/db'

const reviewSchema = z.object({
  employeeId: z.number().min(1, 'Employee is required'),
  reviewerId: z.number().min(1, 'Reviewer is required'),
  reviewCycle: z.enum(['quarterly', 'bi-annual', 'annual']),
  reviewPeriodStart: z.string().min(1, 'Start date is required'),
  reviewPeriodEnd: z.string().min(1, 'End date is required'),
  goals: z.array(z.string()).optional(),
  strengths: z.string().optional(),
  areasOfImprovement: z.string().optional(),
  overallRating: z.number().min(1).max(5).optional(),
  comments: z.string().optional(),
})

type ReviewFormValues = z.infer<typeof reviewSchema>

export const Route = createFileRoute('/hr/performance-reviews')({
  component: PerformanceReviewsPage,
})

function PerformanceReviewsPage() {
  return <ModuleGuard moduleKey="hr"><PerformanceReviewsList /></ModuleGuard>
}

function PerformanceReviewsList() {
  // 'all' is the sentinel for "no filter" — Radix Select doesn't allow
  // empty-string values, so we use a literal 'all' here and coerce to
  // undefined when building the query.
  const [employeeFilter, setEmployeeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [cycleFilter, setCycleFilter] = useState<string>('all')
  const [isFormOpen, setIsFormOpen] = useState(false)

  const navigate = useNavigate()

  const { data: reviewsData, isLoading, refetch } = useQuery(orpc.hr.getPerformanceReviews.queryOptions({
    input: {
      employeeId:
        employeeFilter && employeeFilter !== 'all' ? Number(employeeFilter) : undefined,
      status:
        statusFilter === 'all'
          ? undefined
          : (statusFilter as 'draft' | 'submitted' | 'acknowledged'),
      cycle:
        cycleFilter === 'all'
          ? undefined
          : (cycleFilter as 'quarterly' | 'bi-annual' | 'annual'),
    },
  }))

  const { data: employeesData } = useQuery(orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 100 } }))

  const reviews = (reviewsData ?? []) as HRPerformanceReview[]
  const employees = (employeesData?.data ?? []) as HREmployee[]

  const createMutation = useMutation(orpc.hr.createPerformanceReview.mutationOptions({
    onSuccess: () => {
      toast.success('Performance review created')
      refetch()
      setIsFormOpen(false)
    },
    onError: (e) => toast.error(e.message),
  }))

  const submitMutation = useMutation(orpc.hr.submitPerformanceReview.mutationOptions({
    onSuccess: () => {
      toast.success('Review submitted')
      refetch()
    },
    onError: (e) => toast.error(e.message),
  }))

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const getReviewerName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const handleSubmit = async (data: ReviewFormValues) => {
    await createMutation.mutateAsync(data)
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Performance Reviews</h1>
          <p className="text-muted-foreground">Manage employee performance reviews</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Review
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.firstName} {emp.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={cycleFilter} onValueChange={setCycleFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Cycles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cycles</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="bi-annual">Bi-annual</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No performance reviews found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>Cycle</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow
                    key={review.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: `/hr/performance-reviews/${review.id}` })}
                  >
                    <TableCell>{getEmployeeName(review.employeeId)}</TableCell>
                    <TableCell>{getReviewerName(review.reviewerId)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {review.reviewCycle.replace('-', '/')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {review.reviewPeriodStart && review.reviewPeriodEnd
                        ? `${new Date(review.reviewPeriodStart).toLocaleDateString()} - ${new Date(review.reviewPeriodEnd).toLocaleDateString()}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {review.overallRating ? (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={
                                star <= review.overallRating! ? 'text-yellow-500' : 'text-gray-300'
                              }
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={review.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {review.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              submitMutation.mutate({ id: review.id })
                            }}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Submit
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            void navigate({ to: `/hr/performance-reviews/${review.id}` })
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateReviewDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employees={employees}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

interface CreateReviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: HREmployee[]
  onSubmit: (data: ReviewFormValues) => Promise<void>
}

function CreateReviewDialog({
  open,
  onOpenChange,
  employees,
  onSubmit,
}: CreateReviewDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      reviewCycle: 'quarterly',
      reviewPeriodStart: '',
      reviewPeriodEnd: '',
    },
  })

  const handleSubmit = async (data: ReviewFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Performance Review</DialogTitle>
          <DialogDescription>Create a new performance review for an employee</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reviewerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reviewer</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select reviewer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reviewCycle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Review Cycle</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value as 'quarterly' | 'bi-annual' | 'annual')}
                    value={field.value ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select cycle" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="bi-annual">Bi-annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="reviewPeriodStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period Start</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reviewPeriodEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period End</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="overallRating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Overall Rating (1-5)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="strengths"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Strengths</FormLabel>
                  <FormControl>
                    <Textarea placeholder="List employee strengths..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="areasOfImprovement"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Areas of Improvement</FormLabel>
                  <FormControl>
                    <Textarea placeholder="List areas for improvement..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comments</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional comments..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Review'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
